import { Raycaster, Vector2, Vector3 } from 'three';

import type { EventManagerConfig, SelectionResult, EventCallbacks } from './types';
import { raycastCfn } from './functions/raycast.cfn';
import { setupEventListenersCfn } from './functions/setup-event-listeners.cfn';
import { disposeCfn } from './functions/dispose.cfn';

/**
 * EventManager handles user interaction events:
 * - Click/tap for object selection (a click that ends a drag is ignored — see
 *   `setup-event-listeners.cfn.ts`)
 * - Double-click/double-tap for highlighting
 * - Raycasting for hit detection
 * - Hover detection (optional)
 *
 * @example
 * ```typescript
 * const eventManager = new EventManager({
 *   domElement: canvas,
 *   scene: scene,
 *   camera: camera
 * });
 *
 * eventManager.setCallbacks({
 *   onSelect: (result) => console.log('Selected:', result.objectId),
 *   onDoubleClick: (result, add) => highlightObject(result.objectId, add)
 * });
 *
 * // Cleanup
 * eventManager.dispose();
 * ```
 */
export class EventManager {
	#config: EventManagerConfig;
	#raycaster: Raycaster;
	#pointer = new Vector2();
	#callbacks: EventCallbacks = {};
	#lastSelectionNormal: Vector3 | null = null;
	#lastSelectionObjectId: string | null = null;
	#selectedObjectId: string | null = null;

	// Double-tap tracking
	#lastTapTime = 0;
	#lastTapX = 0;
	#lastTapY = 0;

	// Drag tracking — a camera move (orbit/pan/zoom) that ends over an object
	// must not read as a click on it
	#pointerDownX = 0;
	#pointerDownY = 0;
	#pointerDragged = false;

	// Event handlers (stored for cleanup)
	#handleClick: ((e: MouseEvent) => void) | null = null;
	#handleDoubleClick: ((e: MouseEvent) => void) | null = null;
	#handleContextMenu: ((e: MouseEvent) => void) | null = null;
	#handleTouchStart: ((e: TouchEvent) => void) | null = null;
	#handleMouseMove: ((e: MouseEvent) => void) | null = null;
	#handlePointerDown: ((e: PointerEvent) => void) | null = null;
	#handlePointerMove: ((e: PointerEvent) => void) | null = null;

	constructor(config: EventManagerConfig) {
		this.#config = config;
		this.#raycaster = new Raycaster();

		setupEventListenersCfn(this, {});
	}

	// ── Internal state for cfn access ────────────────────────────────────────
	/** @internal */ get internalConfig(): EventManagerConfig {
		return this.#config;
	}
	/** @internal */ get internalRaycaster(): Raycaster {
		return this.#raycaster;
	}
	/** @internal */ get internalPointer(): Vector2 {
		return this.#pointer;
	}
	/** @internal */ get internalCallbacks(): EventCallbacks {
		return this.#callbacks;
	}
	/** @internal */ set internalCallbacks(callbacks: EventCallbacks) {
		this.#callbacks = callbacks;
	}
	/** @internal */ get internalLastSelectionNormal(): Vector3 | null {
		return this.#lastSelectionNormal;
	}
	/** @internal */ set internalLastSelectionNormal(normal: Vector3 | null) {
		this.#lastSelectionNormal = normal;
	}
	/** @internal */ get internalLastSelectionObjectId(): string | null {
		return this.#lastSelectionObjectId;
	}
	/** @internal */ set internalLastSelectionObjectId(id: string | null) {
		this.#lastSelectionObjectId = id;
	}
	/** @internal */ get internalSelectedObjectId(): string | null {
		return this.#selectedObjectId;
	}
	/** @internal */ set internalSelectedObjectId(id: string | null) {
		this.#selectedObjectId = id;
	}
	/** @internal */ get internalLastTapTime(): number {
		return this.#lastTapTime;
	}
	/** @internal */ set internalLastTapTime(time: number) {
		this.#lastTapTime = time;
	}
	/** @internal */ get internalLastTapX(): number {
		return this.#lastTapX;
	}
	/** @internal */ set internalLastTapX(x: number) {
		this.#lastTapX = x;
	}
	/** @internal */ get internalLastTapY(): number {
		return this.#lastTapY;
	}
	/** @internal */ set internalLastTapY(y: number) {
		this.#lastTapY = y;
	}
	/** @internal */ get internalPointerDownX(): number {
		return this.#pointerDownX;
	}
	/** @internal */ set internalPointerDownX(x: number) {
		this.#pointerDownX = x;
	}
	/** @internal */ get internalPointerDownY(): number {
		return this.#pointerDownY;
	}
	/** @internal */ set internalPointerDownY(y: number) {
		this.#pointerDownY = y;
	}
	/** @internal */ get internalPointerDragged(): boolean {
		return this.#pointerDragged;
	}
	/** @internal */ set internalPointerDragged(dragged: boolean) {
		this.#pointerDragged = dragged;
	}
	/** @internal */ get internalHandleClick(): ((e: MouseEvent) => void) | null {
		return this.#handleClick;
	}
	/** @internal */ set internalHandleClick(handler: ((e: MouseEvent) => void) | null) {
		this.#handleClick = handler;
	}
	/** @internal */ get internalHandleDoubleClick(): ((e: MouseEvent) => void) | null {
		return this.#handleDoubleClick;
	}
	/** @internal */ set internalHandleDoubleClick(handler: ((e: MouseEvent) => void) | null) {
		this.#handleDoubleClick = handler;
	}
	/** @internal */ get internalHandleContextMenu(): ((e: MouseEvent) => void) | null {
		return this.#handleContextMenu;
	}
	/** @internal */ set internalHandleContextMenu(handler: ((e: MouseEvent) => void) | null) {
		this.#handleContextMenu = handler;
	}
	/** @internal */ get internalHandleTouchStart(): ((e: TouchEvent) => void) | null {
		return this.#handleTouchStart;
	}
	/** @internal */ set internalHandleTouchStart(handler: ((e: TouchEvent) => void) | null) {
		this.#handleTouchStart = handler;
	}
	/** @internal */ get internalHandleMouseMove(): ((e: MouseEvent) => void) | null {
		return this.#handleMouseMove;
	}
	/** @internal */ set internalHandleMouseMove(handler: ((e: MouseEvent) => void) | null) {
		this.#handleMouseMove = handler;
	}
	/** @internal */ get internalHandlePointerDown(): ((e: PointerEvent) => void) | null {
		return this.#handlePointerDown;
	}
	/** @internal */ set internalHandlePointerDown(handler: ((e: PointerEvent) => void) | null) {
		this.#handlePointerDown = handler;
	}
	/** @internal */ get internalHandlePointerMove(): ((e: PointerEvent) => void) | null {
		return this.#handlePointerMove;
	}
	/** @internal */ set internalHandlePointerMove(handler: ((e: PointerEvent) => void) | null) {
		this.#handlePointerMove = handler;
	}

	/**
	 * Get the selected object ID
	 */
	get selectedObjectId(): string | null {
		return this.#selectedObjectId;
	}

	/**
	 * Get the last selection normal
	 */
	get lastSelectionNormal(): Vector3 | null {
		return this.#lastSelectionNormal?.clone() ?? null;
	}

	/**
	 * Get/set debug mode
	 */
	get debugMode(): boolean {
		return this.#config.debugMode ?? false;
	}

	set debugMode(enabled: boolean) {
		this.#config.debugMode = enabled;
	}

	/**
	 * Set event callbacks
	 */
	setCallbacks(callbacks: EventCallbacks): void {
		this.#callbacks = { ...this.#callbacks, ...callbacks };
	}

	/**
	 * Perform a raycast at the given screen coordinates
	 */
	raycast(screenX: number, screenY: number): SelectionResult | null {
		return raycastCfn(this, { screenX, screenY });
	}

	/**
	 * Clear the current selection
	 */
	clearSelection(): void {
		this.#selectedObjectId = null;
		this.#lastSelectionNormal = null;
		this.#lastSelectionObjectId = null;
	}

	/**
	 * Dispose of all event listeners
	 */
	dispose(): void {
		disposeCfn(this, {});
	}
}
